import { useEffect, useState, useRef } from "react";
import { Target, MessageCircle, Trophy, Timer, MousePointerClick, Hourglass } from "lucide-react";
import { api } from "@/lib/api";

function fmt72(secs) {
  if (secs <= 0) return { d: "00", h: "00", m: "00", s: "00" };
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return {
    d: String(d).padStart(2, "0"), h: String(h).padStart(2, "0"),
    m: String(m).padStart(2, "0"), s: String(s).padStart(2, "0"),
  };
}

export default function HunterDashboard({ hunter, refreshKey }) {
  const [board, setBoard] = useState([]);
  const [jackpot, setJackpot] = useState({ hunter_prize_usd: 0, seconds_remaining: 0, contest_active: false });
  const [localSecs, setLocalSecs] = useState(0);
  const tickRef = useRef(null);

  const load = async () => {
    try {
      const [b, j] = await Promise.all([
        api.get("/hunters/leaderboard?limit=10"),
        api.get("/jackpot"),
      ]);
      setBoard(b.data);
      setJackpot(j.data);
      setLocalSecs(j.data.seconds_remaining || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    tickRef.current = setInterval(() => setLocalSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(iv); clearInterval(tickRef.current); };
  }, [refreshKey]);

  const myPos = hunter ? board.findIndex((h) => h.hunter_id === hunter.hunter_id) : -1;
  const t = fmt72(localSecs);

  return (
    <section
      id="hunter-dashboard"
      data-testid="hunter-dashboard"
      className="mt-20 rounded-3xl border border-neon-red/30 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 20% 20%, rgba(255,0,60,0.10), transparent 55%), #0A0A0A",
      }}
    >
      <div className="p-6 sm:p-8 lg:p-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-neon-red mb-2">
              <Target className="w-3.5 h-3.5" />
              HUNTER DASHBOARD · TIEMPO REAL
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">
              La caza en vivo
            </h2>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
              {jackpot.contest_active ? "Tiempo restante" : "Concurso"}
            </div>
            {jackpot.contest_active ? (
              <div data-testid="hunter-countdown" className="font-mono font-black tabular-nums text-2xl sm:text-3xl text-neon-red">
                {t.d}<span className="opacity-40 text-lg">d</span> {t.h}:{t.m}:{t.s}
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 text-neon-cyan font-mono text-sm">
                <Hourglass className="w-4 h-4" /> Esperando arranque del admin
              </div>
            )}
            <div className="text-[10px] font-mono text-neon-red mt-1">
              Bolsa Hunter: ${Number(jackpot.hunter_prize_usd || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {hunter && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
            <StatCard label="Tu email" value={hunter.email} mono />
            <StatCard label="Viral points" value={hunter.viral_points_today ?? 0} color="#25D366" icon={MessageCircle} />
            <StatCard label="Unique clicks" value={hunter.unique_clicks_today ?? 0} color="#FF003C" icon={MousePointerClick} />
            <StatCard label="Tu posición" value={myPos >= 0 ? `#${myPos + 1}` : "—"} color="#FFB800" icon={Trophy} />
          </div>
        )}

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
            Ranking Hunter en tiempo real
          </div>
          {board.length === 0 ? (
            <div className="text-sm font-mono text-muted-foreground py-8 text-center border border-dashed border-white/10 rounded-xl">
              Nadie ha compartido perfiles todavía. ¡Sé el primero!
            </div>
          ) : (
            <ul className="space-y-2">
              {board.map((h, i) => {
                const mine = hunter && h.hunter_id === hunter.hunter_id;
                return (
                  <li
                    key={h.hunter_id}
                    data-testid={`hunter-board-row-${i + 1}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border ${
                      mine ? "border-neon-red bg-neon-red/10" : "border-white/5 bg-black/50"
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-heading font-black text-sm ${
                      i === 0 ? "bg-neon-red text-white" : "bg-white/5 text-neon-red border border-neon-red/20"
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-mono truncate">
                        {h.email_masked}
                        {mine && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-neon-red/25 text-neon-red uppercase tracking-widest">Tú</span>}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground mt-0.5 flex gap-3">
                        <span className="text-[#25D366]">{h.viral_points} viral pts</span>
                        <span>{h.unique_clicks} unique clicks</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-xl font-bold text-neon-red leading-none">{h.viral_points}</div>
                      <div className="text-[9px] font-mono uppercase text-muted-foreground">pts</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, color = "#00F0FF", icon: Icon, mono }) {
  return (
    <div className="rounded-xl border border-white/5 bg-black/50 p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
        {Icon && <Icon className="w-3 h-3" style={{ color }} />} {label}
      </div>
      <div className={`${mono ? "font-mono text-sm truncate" : "font-mono text-2xl font-bold tabular-nums"}`} style={{ color: mono ? "#FFFFFF" : color }}>
        {value}
      </div>
    </div>
  );
}
